import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Archive, RotateCcw, EyeOff, CheckCircle2, CarFront } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl } from "../../lib/api";
import { CAR_STATUS_STYLE } from "../CarsPage";

const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;

export default function Cars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/admin/cars").then((res) => setCars(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (c, status) => {
    try {
      await api.post(`/admin/cars/${c.id}/status`, { status });
      toast.success(`${c.title} marked ${status}.`);
      load();
    } catch (e) {
      toast.error("Could not update status.");
    }
  };

  return (
    <div data-testid="admin-cars">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-white text-2xl tracking-tight">Cars For Sale</h1>
          <p className="text-pale/50 text-sm mt-1">{cars.length} vehicle(s) listed.</p>
        </div>
        <Link to="/admin/cars/new" data-testid="add-car-btn" className="btn-primary !py-2.5 text-sm">
          <Plus size={16} /> ADD CAR
        </Link>
      </div>

      <div className="mt-6 bg-navy border border-deep rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-cars-table">
            <thead>
              <tr className="text-left text-pale/40 text-xs uppercase tracking-wider border-b border-deep">
                <th className="px-4 py-3 font-heading">Car</th>
                <th className="px-4 py-3 font-heading">Year</th>
                <th className="px-4 py-3 font-heading">Mileage</th>
                <th className="px-4 py-3 font-heading text-right">Price</th>
                <th className="px-4 py-3 font-heading">Status</th>
                <th className="px-4 py-3 font-heading text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-deep/60">
              {cars.map((c) => (
                <tr key={c.id} data-testid={`admin-car-row-${c.id}`} className="text-pale/80 align-middle">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-10 rounded overflow-hidden bg-deep/50 shrink-0 flex items-center justify-center">
                        {c.images?.length > 0 ? <img src={imgUrl(c.images[c.main_image] || c.images[0])} alt="" className="w-full h-full object-cover" loading="lazy" /> : <CarFront size={18} className="text-pale/30" />}
                      </div>
                      <p className="font-medium text-white truncate max-w-[240px]">{c.title}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-pale/60">{c.year || "-"}</td>
                  <td className="px-4 py-3 text-pale/60">{c.mileage || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-sky">{fmt(c.price)}</td>
                  <td className="px-4 py-3"><span className={`status-chip ${CAR_STATUS_STYLE[c.status]}`}>{c.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <Link to={`/admin/cars/${c.id}/edit`} data-testid={`edit-car-${c.id}`} title="Edit" className="p-2 rounded text-pale/60 hover:text-white hover:bg-deep transition-colors"><Pencil size={15} /></Link>
                      {c.status !== "AVAILABLE" && (
                        <button onClick={() => setStatus(c, "AVAILABLE")} data-testid={`available-car-${c.id}`} title="Mark available" className="p-2 rounded text-pale/60 hover:text-emerald-400 hover:bg-deep transition-colors"><RotateCcw size={15} /></button>
                      )}
                      {c.status !== "SOLD" && (
                        <button onClick={() => setStatus(c, "SOLD")} data-testid={`sold-car-${c.id}`} title="Mark sold" className="p-2 rounded text-pale/60 hover:text-sky hover:bg-deep transition-colors"><CheckCircle2 size={15} /></button>
                      )}
                      {c.status !== "HIDDEN" ? (
                        <button onClick={() => setStatus(c, "HIDDEN")} data-testid={`archive-car-${c.id}`} title="Hide from site" className="p-2 rounded text-pale/60 hover:text-white hover:bg-deep transition-colors"><Archive size={15} /></button>
                      ) : (
                        <button onClick={() => setStatus(c, "AVAILABLE")} data-testid={`unhide-car-${c.id}`} title="Restore" className="p-2 rounded text-pale/60 hover:text-white hover:bg-deep transition-colors"><EyeOff size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && cars.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-pale/40">No cars listed yet. Click ADD CAR to list your first vehicle.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
